import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { Outlet, useParams } from 'react-router-dom';
import { createLabPublicOcApi } from '../../utils/ocRequestApi';
import { logInfo } from '../../config_and_helpers/logger';
import OcWorkspaceShell from './OcWorkspaceShell';
import PublicOrganoidFormShell from './PublicOrganoidFormShell';

const LOG_CONTEXT = 'OcLabAccess';

const OcLabAccessContext = createContext(null);

/**
 * @returns {Object|null}
 */
export function useOcLabAccess() {
  return useContext(OcLabAccessContext);
}

/**
 * Provider for magic-link lab access (no Materiah login).
 */
export function OcLabAccessProvider({ labToken, children }) {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);

  const api = useMemo(
    () => (labToken ? createLabPublicOcApi(labToken) : null),
    [labToken]
  );

  const reload = useCallback(async () => {
    if (!api) return null;
    setLoading(true);
    setAccessError(null);
    const result = await api.getDetail();
    setLoading(false);
    if (!result.success) {
      if (result.status === 403 || result.status === 404) {
        setAccessError(
          result.messages?.[0] ||
            'This link is invalid, expired, or has been revoked.'
        );
        setRequest(null);
        return null;
      }
      setAccessError(result.messages?.[0] || 'Failed to load request.');
      return null;
    }
    setRequest(result.data);
    logInfo(LOG_CONTEXT, 'lab public detail loaded', {
      requestId: result.data?.id,
    });
    return result.data;
  }, [api]);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(
    () => ({
      mode: 'labPublic',
      labToken,
      api,
      request,
      setRequest,
      reload,
      loading,
      accessError,
      basePath: `/organoid-requests/access/${labToken}`,
      showInboxLink: false,
    }),
    [labToken, api, request, reload, loading, accessError]
  );

  return (
    <OcLabAccessContext.Provider value={value}>
      {children}
    </OcLabAccessContext.Provider>
  );
}

/**
 * Dead-link screen for expired/revoked lab magic links — never redirects to login.
 */
export function OcLabAccessDeadLink({ message }) {
  return (
    <PublicOrganoidFormShell title="Link unavailable">
      <p className="mb-0 text-danger">
        {message ||
          'This secure email link is invalid, expired, or has been revoked. Contact the lab if you still need access.'}
      </p>
    </PublicOrganoidFormShell>
  );
}

/**
 * Layout for /organoid-requests/access/:labToken/* — banner + nested stage pages.
 */
export function OcLabAccessLayout() {
  const { labToken } = useParams();

  if (!labToken) {
    return <OcLabAccessDeadLink message="Missing access token in the link." />;
  }

  return (
    <OcLabAccessProvider labToken={labToken}>
      <OcLabAccessShell />
    </OcLabAccessProvider>
  );
}

function OcLabAccessShell() {
  const ctx = useOcLabAccess();

  if (ctx.loading && !ctx.request && !ctx.accessError) {
    return (
      <div className="oc-page">
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (ctx.accessError) {
    return <OcLabAccessDeadLink message={ctx.accessError} />;
  }

  return (
    <OcWorkspaceShell
      showLogos
      wide
      theme="yellow"
      banner={
        <div className="oc-access-banner mb-3" role="status">
          You are viewing this request via a secure email link. Anyone with this
          link can update this request. Do not forward it unless intended.
        </div>
      }
    >
      <Outlet />
    </OcWorkspaceShell>
  );
}

export default OcLabAccessLayout;
